import ArticleCardContent from "@src/app/components/Blocks/ArticleCardContent/ArticleCardContent";
import NotFound from "@src/app/components/Blocks/NotFound/NotFound";
import Pagination from "@src/app/components/Blocks/Pagination/Pagination";
import AdSettings from "@src/app/components/Elements/Advertisement/AdSettings";
import CatfishAd from "@src/app/components/Elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@src/app/components/Elements/Advertisement/helpers";
import PrestitialAd from "@src/app/components/Elements/Advertisement/PrestitialAd";
import GAData from "@src/app/components/Elements/GAData/GAData";
import Spinner from "@src/app/components/Elements/Spinner/Spinner";
import BlockTitle from "@src/app/components/Elements/Typography/BlockTitle/BlockTitle";
import MainContainer from "@src/app/components/Layouts/MainContainer/MainContainer";
import StandardCardLayout from "@src/app/components/Layouts/StandardCardLayout/StandardCardLayout";
import ContributorProfile from "@src/app/components/Modules/ContributorPage/ContributorProfile/ContributorProfile";
import formatPublishedDate from "@src/app/helper/formatPublishedDate";
import useContributorApi from "@src/app/hooks/useContributorApi";
import useContributorArticlesApi from "@src/app/hooks/useContributorArticlesApi";
import useContributorContext from "@src/app/hooks/useContributorContext";
import useGlobalAdSetting from "@src/app/hooks/useGlobalAdSetting";
import { ArticleCard } from "@src/app/types/Article";
import { OutletContextType } from "@src/app/types/OutletContextType";
import { logEnd, logStart } from "@src/app/utils/helpers";
import { useEffect } from "react";
import { useLocation, useOutletContext, useSearchParams } from "react-router";

import styles from "./Contributor.module.css";

export default function ContributorPage() {
  const { setIsLoading } = useOutletContext<OutletContextType>(); // Access parent isLoading setter
  const { isPrestitialEnabled, topOverlayImpressions, isCatfishEnabled } =
    useGlobalAdSetting();

  const tResponse = useContributorContext();
  const statusCode = tResponse.statusCode;
  const payload = tResponse.payload;

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 0;

  const timeStartMain = logStart();
  const { data: apiPayloadContributor, isLoading } = useContributorApi({
    pathname: pathname,
  });
  logEnd("Contributor useContributorApi", timeStartMain);

  const timeStartArticleApi = logStart();
  const { data: apiPayloadArticles } = useContributorArticlesApi({
    pathname,
    page,
  });
  logEnd("Contributor useContributorArticlesApi", timeStartArticleApi);

  // Notify parent component about the loading state
  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  if (statusCode != 200 || !payload || typeof payload === "string") {
    return <NotFound />;
  }

  const pathnameValue = pathname.split("/").filter(Boolean);
  const level2 = pathnameValue[0];

  const contributor =
    apiPayloadContributor?.data.results || payload.contributor || undefined;
  const contributorName = contributor?.name as string;
  const totalArticles =
    apiPayloadArticles?.data.totalResults ||
    payload.contributorArticles?.totalResults ||
    0;
  const articles =
    apiPayloadArticles?.data.results ||
    payload.contributorArticles?.results ||
    [];
  const totalPages = Math.ceil(totalArticles / 9);

  return (
    <>
      <AdSettings />

      <GAData
        title={contributorName}
        level2={level2}
        contenttype="index/listing page"
        pagination={page}
      />

      <PrestitialAd
        slot={GoogleAdsSlotFactory.prestitial("authors")}
        isPrestitialEnabled={isPrestitialEnabled}
        topOverlayImpressions={topOverlayImpressions}
      />

      {contributor && (
        <ContributorProfile {...contributor} pathname={pathname} />
      )}

      <MainContainer>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.contributorArticleListTitle}>
              <BlockTitle hasBorder>
                {contributor ? `Articles by ${contributor.name}` : ""}
              </BlockTitle>
            </div>

            <StandardCardLayout>
              {articles.length > 0 &&
                articles.map((article: ArticleCard) => {
                  const {
                    id,
                    articleName,
                    articlePath,
                    authors,
                    category,
                    publishedDate,
                    imgUrl,
                    imgWidth,
                    imgHeight,
                    blurb,
                    displayType,
                  } = article;
                  return (
                    <ArticleCardContent
                      key={id}
                      articleTitle={articleName}
                      articlePath={articlePath}
                      authors={authors}
                      categoryLabelProps={{
                        href: category.path,
                        label: category.name,
                      }}
                      date={formatPublishedDate(publishedDate)}
                      hasAuthorAndDate
                      hasBackground
                      hasSidePadding
                      hasSnippet
                      hasTitleMargin
                      imgUrl={imgUrl}
                      imgWidth={imgWidth}
                      imgHeight={imgHeight}
                      paragraphContent={blurb}
                      isVideo={displayType == "video"}
                      displayType={displayType}
                    ></ArticleCardContent>
                  );
                })}
            </StandardCardLayout>

            <div className={styles.pagination}>
              <Pagination isUseQueryParams totalPages={totalPages} />
            </div>
          </>
        )}
      </MainContainer>

      <CatfishAd
        slot={GoogleAdsSlotFactory.catfish("authors")}
        isCatfishEnabled={isCatfishEnabled}
      />
    </>
  );
}
